import React, { useContext, useState } from "react";
import { submitProviderInvoice } from "api/ProviderInvoice";
import { Modal, ModalBody, ModalHeader, ModalFooter } from "components/ui/Modal";
import { Box, Flex } from "theme-ui";
import P from "components/typography/P";
import BasicButton from "components/forms/BasicButton";
import FileInput from "components/forms/FileInput";
import TextInput from "components/forms/TextInput";
import { themevals } from "theme/themevals";
import UserContext from "context/User";
import { useToast } from "components/toast";
import { SubmitIcon } from "components/svg";

interface SubmitInvoiceProps {
  po: any;
  isOpen: boolean;
  toggle: () => void;
  onSuccess: () => void;
}

const SubmitInvoice: React.FC<SubmitInvoiceProps> = ({
  po,
  isOpen,
  toggle,
  onSuccess,
}) => {
  const { user } = useContext<any>(UserContext);
  const company = user?.company || user?.provider?.company;
  // Company users submit on behalf of a provider (billing@ forwards) whereas
  // provider users submit their own invoices. The modal copy shifts accordingly.
  const isCompanyUser = user?.accessLevel === 'isCompany';
  const providerName = (po && po.provider && po.provider.name) || '';
  const toast = useToast();
  const [fileValue, setFileValue] = useState<any>(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [amount, setAmount] = useState<string>("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFileChange = (val: any) => {
    // FileInput returns an array of {file, url, title, location}
    if (Array.isArray(val) && val.length > 0) {
      setFileValue(val[0]);
    } else {
      setFileValue(null);
    }
  };

  const handleSubmit = async () => {
    if (!fileValue?.url || !fileValue?.file) {
      toast.add("Please upload an invoice file", themevals.colors.danger);
      return;
    }

    if (!invoiceNumber.trim()) {
      toast.add("Please enter your invoice number", themevals.colors.danger);
      return;
    }

    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
      toast.add("Please enter a valid invoice amount", themevals.colors.danger);
      return;
    }

    setIsSubmitting(true);

    try {
      // First, upload the file to S3 using the presigned URL
      const uploadResponse = await fetch(fileValue.url, {
        method: "PUT",
        headers: { "Content-Type": fileValue.file.type },
        body: fileValue.file,
      });

      if (!uploadResponse.ok) {
        toast.add("Failed to upload file", themevals.colors.danger);
        setIsSubmitting(false);
        return;
      }

      // Now submit the invoice to the backend
      const response = await submitProviderInvoice({
        purchaseOrderId: po.id,
        filePath: fileValue.location,
        fileName: fileValue.title || fileValue.file?.name,
        invoiceNumber: invoiceNumber.trim(),
        amount: parseFloat(amount),
        invoiceDate: invoiceDate || undefined,
      });

      if (response.success) {
        const hasWarnings = response.warnings && response.warnings.length > 0;
        let message: string;
        if (hasWarnings) {
          message = 'Invoice submitted. ' + response.warnings[0];
        } else if (isCompanyUser) {
          message = 'Invoice submitted on behalf of ' + (providerName || 'provider') + '. It is now pending approval.';
        } else {
          message = 'Invoice submitted successfully! It is now pending approval.';
        }
        toast.add(message, hasWarnings ? themevals.colors.warning : themevals.colors.success);
        toggle();
        onSuccess();
      } else {
        toast.add(response.error || "Failed to submit invoice", themevals.colors.danger);
      }
    } catch (e: any) {
      toast.add(e?.message || "An error occurred while submitting the invoice", themevals.colors.danger);
    }

    setIsSubmitting(false);
  };

  // Calculate PO total from line items for display
  const poTotal = (po.lineitems || []).reduce((sum: number, item: any) => {
    return sum + (Number(item.quantity) || 0) * (Number(item.cost) || 0);
  }, 0);

  const poNumber = `GMO-${String(po.sequence || "").padStart(5, "0")}`;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="sm" centered>
      <ModalHeader toggle={toggle}></ModalHeader>

      <ModalBody>
        <Box>
          <P marginBottom="10px">
            {isCompanyUser ? (
              <>Upload provider invoice for <strong>{providerName || 'this provider'}</strong> on Purchase Order <strong>{poNumber}</strong>.</>
            ) : (
              <>Upload your invoice for Purchase Order <strong>{poNumber}</strong>.</>
            )}
          </P>

          {isCompanyUser && (
            <P fontSize="12px" fontStyle="italic" color={themevals.colors.dkgrey} marginBottom="10px">
              Submitting on behalf of a provider. Recorded as a staff upload; flows to Zoho as a bill on approval.
            </P>
          )}

          {poTotal > 0 && (
            <P fontSize="13px" color={themevals.colors.dkgrey} marginBottom="5px">
              PO Total: <strong>${poTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}</strong>
            </P>
          )}

          <P fontSize="13px" color={themevals.colors.dkgrey} marginBottom="20px">
            Accepted formats: PDF, JPG, PNG
          </P>

          <Box sx={{ marginBottom: "16px" }}>
            <TextInput
              label="Invoice Number"
              name="invoiceNumber"
              value={invoiceNumber}
              onChange={(val: any) => setInvoiceNumber(val || "")}
              placeholder="e.g. INV-12345"
              required
            />
          </Box>

          <Flex sx={{ gap: "12px", marginBottom: "16px" }}>
            <Box sx={{ flex: 1 }}>
              <TextInput
                label="Invoice Amount"
                name="amount"
                type="number"
                value={amount}
                onChange={(val: any) => setAmount(val === "" ? "" : String(val))}
                placeholder="0.00"
                prefix="$"
                min={0}
                step="0.01"
                required
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextInput
                label="Invoice Date"
                name="invoiceDate"
                type="date"
                value={invoiceDate}
                onChange={(val: any) => setInvoiceDate(val || "")}
              />
            </Box>
          </Flex>

          <FileInput
            directory={`companies/${company?.id}/provider-invoices`}
            onChange={handleFileChange}
            refreshValue={isSubmitting}
            multiple={false}
          />
        </Box>
      </ModalBody>

      <ModalFooter>
        <BasicButton scheme="secondaryOutline" onClick={toggle} disabled={isSubmitting}>
          Cancel
        </BasicButton>
        <BasicButton
          onClick={handleSubmit}
          $submitting={isSubmitting}
          disabled={!fileValue || !invoiceNumber.trim() || !amount}
          keepText={true}
          $loadingColor={themevals.colors.white}
        >
          {!isSubmitting && <SubmitIcon fill={"#fff"} direction="up" />}
          Submit Invoice
        </BasicButton>
      </ModalFooter>
    </Modal>
  );
};

export default SubmitInvoice;
